import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import BillingSetupModal from '../../components/payment/BillingSetupModal';
import { paymentService } from '../../services/payments';

const AgentAccountSettings = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    displayName: '',
    phone: '',
    whatIOffer: [],
    countriesOperated: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [billingStatus, setBillingStatus] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);

  // Define travel categories
  const travelCategories = [
    { id: 'beach', label: 'Beach' },
    { id: 'city_break', label: 'City Break' },
    { id: 'family', label: 'Family' },
    { id: 'couples_romantic', label: 'Couples / Romantic Getaway' },
    { id: 'honeymoon', label: 'Honeymoon' },
    { id: 'short_break', label: 'Short Break / Weekend Away' },
    { id: 'all_inclusive', label: 'All-Inclusive Resort' },
    { id: 'luxury', label: 'Luxury' },
    { id: 'golf', label: 'Golf' },
    { id: 'ski_snow', label: 'Ski / Snow' },
    { id: 'cruise', label: 'Cruise' },
    { id: 'adventure', label: 'Adventure' },
    { id: 'wellness_spa', label: 'Wellness & Spa Retreat' },
    { id: 'safari_wildlife', label: 'Safari / Wildlife' },
    { id: 'food_wine', label: 'Food & Wine' },
    { id: 'multi_centre', label: 'Multi-Centre' },
    { id: 'cultural_heritage', label: 'Cultural & Heritage' },
    { id: 'group', label: 'Group' }
  ];

  // All countries list (same as DestinationSelector)
  const allCountries = [
    // Europe
    'United Kingdom', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Austria',
    'Belgium', 'Portugal', 'Greece', 'Norway', 'Sweden', 'Denmark', 'Finland', 'Iceland',
    'Ireland', 'Poland', 'Czech Republic', 'Hungary', 'Croatia', 'Slovenia', 'Slovakia',
    'Romania', 'Bulgaria', 'Estonia', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Cyprus',
    // Asia
    'Japan', 'China', 'India', 'Thailand', 'Singapore', 'Malaysia', 'Indonesia', 'Philippines',
    'Vietnam', 'Cambodia', 'Laos', 'Myanmar', 'South Korea', 'Taiwan', 'Hong Kong', 'Macau',
    'Nepal', 'Bhutan', 'Sri Lanka', 'Maldives', 'Bangladesh', 'Pakistan', 'Afghanistan',
    'Iran', 'Iraq', 'Jordan', 'Lebanon', 'Syria', 'Turkey', 'Israel', 'Palestine', 'Saudi Arabia',
    'UAE', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Yemen', 'Kazakhstan', 'Uzbekistan',
    'Kyrgyzstan', 'Tajikistan', 'Turkmenistan', 'Mongolia', 'North Korea',
    // North America
    'United States', 'Canada', 'Mexico', 'Guatemala', 'Belize', 'El Salvador', 'Honduras',
    'Nicaragua', 'Costa Rica', 'Panama', 'Cuba', 'Jamaica', 'Haiti', 'Dominican Republic',
    'Puerto Rico', 'Trinidad and Tobago', 'Barbados', 'Bahamas', 'Antigua and Barbuda',
    'Saint Lucia', 'Grenada', 'Saint Vincent and the Grenadines', 'Dominica', 'Saint Kitts and Nevis',
    // South America
    'Brazil', 'Argentina', 'Chile', 'Peru', 'Colombia', 'Venezuela', 'Ecuador', 'Bolivia',
    'Paraguay', 'Uruguay', 'Guyana', 'Suriname', 'French Guiana',
    // Africa
    'South Africa', 'Kenya', 'Tanzania', 'Morocco', 'Egypt', 'Tunisia', 'Algeria', 'Libya',
    'Sudan', 'Ethiopia', 'Somalia', 'Djibouti', 'Eritrea', 'Uganda', 'Rwanda', 'Burundi',
    'Democratic Republic of Congo', 'Republic of Congo', 'Central African Republic', 'Chad',
    'Cameroon', 'Nigeria', 'Niger', 'Mali', 'Burkina Faso', 'Ghana', 'Togo', 'Benin',
    'Ivory Coast', 'Liberia', 'Sierra Leone', 'Guinea', 'Guinea-Bissau', 'Senegal',
    'Gambia', 'Mauritania', 'Western Sahara', 'Cape Verde', 'São Tomé and Príncipe',
    'Equatorial Guinea', 'Gabon', 'Angola', 'Zambia', 'Zimbabwe', 'Botswana', 'Namibia',
    'Lesotho', 'Swaziland', 'Madagascar', 'Mauritius', 'Seychelles', 'Comoros', 'Mayotte',
    // Oceania
    'Australia', 'New Zealand', 'Fiji', 'Papua New Guinea', 'New Caledonia', 'Solomon Islands',
    'Vanuatu', 'Samoa', 'Tonga', 'Kiribati', 'Palau', 'Marshall Islands', 'Micronesia',
    'Nauru', 'Tuvalu', 'Cook Islands', 'French Polynesia', 'American Samoa', 'Guam',
    'Northern Mariana Islands',
    // Antarctica
    'Antarctica'
  ];

  // Load agent profile on mount
  useEffect(() => {
    const loadAgentProfile = async () => {
      if (!user?.id) {
        console.log('[Agent Settings] No user ID, waiting for authentication...');
        setLoading(false);
        return;
      }

      console.log('[Agent Settings] Loading profile for agent:', user?.id);
      setLoading(true);
      setError(null);

      try {
        // Fetch user profile
        const { data: userProfileData, error: userError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

        if (userError && userError?.code !== 'PGRST116') {
          console.error('[Agent Settings] Error fetching user profile:', userError);
          throw userError;
        }

        // Fetch or create agent profile
        let agentProfileData;
        const { data: existingAgentProfile, error: agentFetchError } = await supabase?.from('agent_profiles')?.select('*')?.eq('user_id', user?.id)?.single();

        if (agentFetchError && agentFetchError?.code !== 'PGRST116') {
          console.error('[Agent Settings] Error fetching agent profile:', agentFetchError);
          throw agentFetchError;
        }

        if (existingAgentProfile) {
          agentProfileData = existingAgentProfile;
          console.log('[Agent Settings] Agent profile found:', agentProfileData);
        } else {
          // Create agent profile if it doesn't exist
          console.log('[Agent Settings] Creating new agent profile...');
          const { data: createdAgentProfile, error: createError } = await supabase?.from('agent_profiles')?.insert({
              user_id: user?.id,
              specializations: [],
              availability_status: 'available'
            })?.select()?.single();

          if (createError) {
            console.error('[Agent Settings] Error creating agent profile:', createError);
            throw createError;
          }

          agentProfileData = createdAgentProfile;
          console.log('[Agent Settings] Agent profile created:', agentProfileData);
        }

        // Load profile data
        setProfile({
          displayName: userProfileData?.full_name || '',
          phone: userProfileData?.phone || '',
          whatIOffer: agentProfileData?.specializations || [],
          countriesOperated: agentProfileData?.countries_operated || []
        });
      } catch (err) {
        console.error('[Agent Settings] Error loading profile:', err);
        setError({
          message: err?.message || 'Failed to load profile',
          details: err?.details || 'Please try refreshing the page'
        });
      } finally {
        setLoading(false);
      }
    };

    loadAgentProfile();
  }, [user?.id]);

  // Load billing status
  useEffect(() => {
    const loadBillingStatus = async () => {
      if (!user?.id) return;

      setLoadingBilling(true);
      try {
        const status = await paymentService?.getAgentBillingStatus(user?.id);
        setBillingStatus(status);
      } catch (err) {
        console.error('[Agent Settings] Error loading billing status:', err);
      } finally {
        setLoadingBilling(false);
      }
    };

    loadBillingStatus();
  }, [user?.id]);

  const handleInputChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value
    }));
    if (successMessage) setSuccessMessage('');
  };

  const handleCategoryToggle = (categoryId) => {
    setProfile((prev) => {
      const currentOffers = prev?.whatIOffer || [];
      const isSelected = currentOffers?.includes(categoryId);
      
      return {
        ...prev,
        whatIOffer: isSelected
          ? currentOffers?.filter(id => id !== categoryId)
          : [...currentOffers, categoryId]
      };
    });
    if (successMessage) setSuccessMessage('');
  };

  const handleCountryToggle = (country) => {
    setProfile((prev) => {
      const currentCountries = prev?.countriesOperated || [];
      const isSelected = currentCountries?.includes(country);
      
      return {
        ...prev,
        countriesOperated: isSelected
          ? currentCountries?.filter(c => c !== country)
          : [...currentCountries, country]
      };
    });
    if (successMessage) setSuccessMessage('');
  };

  const handleSave = async () => {
    if (!user?.id) {
      setError({ message: 'Not authenticated' });
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      console.log('[Agent Settings] Saving profile...');

      // Update user_profiles
      const { error: userUpdateError } = await supabase?.from('user_profiles')?.update({
          full_name: profile?.displayName?.trim() || user?.email?.split('@')?.[0] || 'Agent',
          phone: profile?.phone?.trim() || ''
        })?.eq('id', user?.id);

      if (userUpdateError) {
        console.error('[Agent Settings] Error updating user profile:', userUpdateError);
        throw userUpdateError;
      }

      // Update agent_profiles
      const { error: agentUpdateError } = await supabase?.from('agent_profiles')?.update({
          specializations: profile?.whatIOffer || [],
          countries_operated: profile?.countriesOperated || []
        })?.eq('user_id', user?.id);

      if (agentUpdateError) {
        console.error('[Agent Settings] Error updating agent profile:', agentUpdateError);
        throw agentUpdateError;
      }

      console.log('[Agent Settings] Profile updated successfully');
      setSuccessMessage('Settings saved successfully! Your enquiry matches will now reflect these changes.');

      // Refresh auth context
      if (refreshProfile) {
        await refreshProfile();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('[Agent Settings] Error saving profile:', err);
      setError({
        message: err?.message || 'Failed to save settings',
        details: err?.details || 'Please try again'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBillingSetupSuccess = async (setupIntent) => {
    console.log('[Agent Settings] Payment method setup succeeded, refreshing billing status...');
    setSuccessMessage('Payment method saved successfully!');
    
    // Retry logic: Wait for webhook to process and update database
    // Try fetching billing status with increasing delays
    const maxRetries = 5;
    let retryCount = 0;
    let billingUpdated = false;

    while (retryCount < maxRetries && !billingUpdated) {
      // Wait before checking (exponential backoff: 500ms, 1s, 2s, 3s, 4s)
      const delay = retryCount === 0 ? 500 : retryCount * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.log(`[Agent Settings] Checking billing status (attempt ${retryCount + 1}/${maxRetries})...`);
      
      try {
        const status = await paymentService?.getAgentBillingStatus(user?.id);
        console.log('[Agent Settings] Billing status fetched:', status);
        
        // Check if the payment method ID matches the one we just saved
        if (status?.billing_ready && status?.stripe_payment_method_id === setupIntent?.payment_method) {
          console.log('[Agent Settings] Billing status updated successfully!');
          setBillingStatus(status);
          billingUpdated = true;
          break;
        }
      } catch (err) {
        console.error('[Agent Settings] Error fetching billing status:', err);
      }
      
      retryCount++;
    }

    if (!billingUpdated) {
      console.warn('[Agent Settings] Billing status not updated after retries, forcing refresh...');
      // Force one final refresh
      const status = await paymentService?.getAgentBillingStatus(user?.id);
      setBillingStatus(status);
    }

    setTimeout(() => setSuccessMessage(''), 5000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 px-4 lg:px-6 py-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/agent-dashboard')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Back to dashboard"
            >
              <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
            </button>
            <h1 className="text-2xl font-bold text-foreground">Agent Settings</h1>
          </div>
          <p className="text-muted-foreground pl-11">
            Manage your profile, specializations, and integrations
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-destructive">{error?.message}</p>
                {error?.details && (
                  <p className="text-sm text-destructive/80 mt-1">{error?.details}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-accent/10 border border-accent rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-accent mt-0.5" />
              <p className="font-medium text-accent">{successMessage}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          {/* Profile Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Profile Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Icon name="Lock" size={16} className="text-muted-foreground" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Display Name *
                </label>
                <Input
                  type="text"
                  value={profile?.displayName}
                  onChange={(e) => handleInputChange('displayName', e?.target?.value)}
                  placeholder="Your business/display name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={profile?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  placeholder="+44 7700 900123"
                />
              </div>
            </div>
          </div>

          {/* What I Offer */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-foreground">What I Offer</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select your travel specializations. This determines which enquiries you'll see in your dashboard.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {travelCategories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => handleCategoryToggle(category?.id)}
                  className={`
                    p-3 rounded-lg border-2 text-left transition-all
                    ${
                      profile?.whatIOffer?.includes(category?.id)
                        ? 'border-accent bg-accent/10 text-accent' :'border-border hover:border-accent/50 text-foreground'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{category?.label}</span>
                    {profile?.whatIOffer?.includes(category?.id) && (
                      <Icon name="Check" size={16} className="text-accent" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            {profile?.whatIOffer?.length === 0 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Select at least one category to start receiving matching enquiries
              </p>
            )}
          </div>

          {/* NEW: Countries You Operate In */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-foreground">Countries you operate in</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Only receive enquiries for countries you actually work in.
              </p>
            </div>

            {/* Selected Countries Count */}
            {profile?.countriesOperated?.length > 0 && (
              <div className="mb-4 px-3 py-2 bg-primary/10 rounded-lg inline-block">
                <span className="text-sm font-medium text-primary">
                  {profile?.countriesOperated?.length} countries selected
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-80 overflow-y-auto p-2">
              {allCountries?.map((country) => (
                <button
                  key={country}
                  onClick={() => handleCountryToggle(country)}
                  className={`
                    p-3 rounded-lg border-2 text-left transition-all
                    ${
                      profile?.countriesOperated?.includes(country)
                        ? 'border-accent bg-accent/10 text-accent' :'border-border hover:border-accent/50 text-foreground'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{country}</span>
                    {profile?.countriesOperated?.includes(country) && (
                      <Icon name="Check" size={16} className="text-accent" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            
            {profile?.countriesOperated?.length === 0 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Select at least one country to start receiving matching enquiries
              </p>
            )}
          </div>

          {/* Notifications */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Notifications</h2>
            <Button
              variant="outline"
              onClick={() => navigate('/agent-notification-centre')}
              className="w-full justify-between"
            >
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={18} />
                <span>Notification Centre</span>
              </div>
              <Icon name="ChevronRight" size={18} />
            </Button>
          </div>

          {/* Billing / Payment Method Section */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="CreditCard" size={20} />
                  <span>Billing / Payment Method</span>
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage your payment method for enquiry acceptance
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  setLoadingBilling(true);
                  const status = await paymentService?.getAgentBillingStatus(user?.id);
                  setBillingStatus(status);
                  setLoadingBilling(false);
                  setSuccessMessage('Billing status refreshed');
                  setTimeout(() => setSuccessMessage(''), 3000);
                }}
                disabled={loadingBilling}
                iconName="RefreshCw"
                iconClassName={loadingBilling ? "animate-spin" : ""}
                iconPosition="left"
              >
                Refresh
              </Button>
            </div>

            {loadingBilling ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="ml-3 text-gray-600">Loading billing information...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    {billingStatus?.billing_ready ? (
                      <>
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-green-700">Payment method on file</span>
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm font-medium text-yellow-700">No payment method</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Helper Text */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                  <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-900">
                      You're only charged when you accept an enquiry. The fee is £7.00 per enquiry acceptance.
                    </p>
                  </div>
                </div>

                {/* Error Display */}
                {billingStatus?.billing_status === 'payment_failed' && billingStatus?.last_payment_error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                    <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-red-900 font-medium">Last payment failed</p>
                      <p className="text-sm text-red-800 mt-1">{billingStatus?.last_payment_error}</p>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div>
                  <Button
                    onClick={() => setShowBillingModal(true)}
                    iconName={billingStatus?.billing_ready ? "RefreshCw" : "Plus"}
                    iconPosition="left"
                    className="w-full sm:w-auto"
                  >
                    {billingStatus?.billing_ready ? 'Update payment method' : 'Add payment method'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Save Changes */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/agent-dashboard')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={
                saving || 
                !profile?.displayName?.trim() || 
                profile?.whatIOffer?.length === 0 ||
                profile?.countriesOperated?.length === 0
              }
              className="bg-accent hover:bg-accent/90"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-accent-foreground border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={18} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </main>

      {/* Billing Setup Modal */}
      <BillingSetupModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        agentId={user?.id}
        onSuccess={handleBillingSetupSuccess}
      />
    </div>
  );
};

export default AgentAccountSettings;